import type { FC } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import { createSelector } from '@reduxjs/toolkit';
import cn from 'classnames';
import { useDispatch, useSelector } from 'react-redux';

import { _isNotBlank } from '@/littledash';

import { featuresSelector } from '@/support/Selectors';

import ReasonForChange from '@/components/Shared/Latest/ReasonForChange';
import Spinner from '@/components/UI/Spinner';

interface AsyncConfirmDeleteProps {
  onClick: (reason?: string) => Promise<void>;
  title: string;
  text: string;
  btnTxt: string;
  textOverride?: string;
  showReasonForChange?: boolean;
}

interface FormDataProps {
  confirm: string;
  reason_for_change: string;
}

const featureSelector = createSelector([featuresSelector], (features) => ({
  reasonForChange: features?.reason_for_change ?? false,
}));

const AsyncConfirmDelete: FC<AsyncConfirmDeleteProps> = ({
  onClick,
  title,
  text,
  btnTxt,
  textOverride,
  showReasonForChange,
}) => {
  const { reasonForChange } = useSelector(featureSelector);
  const dispatch = useDispatch();

  const formMethods = useForm<FormDataProps>({
    defaultValues: {
      confirm: '',
      reason_for_change: '',
    },
  });

  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
  } = formMethods;

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  const onSubmit = async ({ reason_for_change }: FormDataProps) => {
    const shouldPassReason = reasonForChange === true && showReasonForChange === true;
    try {
      await onClick(shouldPassReason ? reason_for_change : undefined);
    } catch {
      // The caller reports the failure; keep the modal open so the delete can be retried.
      return;
    }
    closeModal();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(onSubmit)} className="ui__narrow-modal" data-testid="confirm-delete__modal">
        <div className="pb3 tc">
          <h3 className="normal f4 pb3" data-testid="confirm-delete__modal-header">
            {title}
          </h3>
          <p className="f6 pb2" data-testid="confirm-delete__modal-subheader">
            {_isNotBlank(textOverride) ? textOverride : text}
          </p>
        </div>
        <fieldset
          className={cn('pb3 ma0 bn', { ui__disabled: isSubmitting })}
          disabled={isSubmitting}
          aria-busy={isSubmitting}
        >
          {reasonForChange === true && showReasonForChange === true ? (
            <ReasonForChange data-testid="confirm-delete__modal-reason-for-change" isDelete />
          ) : (
            <>
              <label htmlFor="confirm" className="lh-copy" data-testid="confirm-delete__modal-label">
                Type DELETE into the field to confirm
              </label>
              <input
                type="text"
                id="confirm"
                data-testid="confirm-delete__modal-input"
                {...register('confirm', { validate: (value: string) => value === 'DELETE' })}
              />
              <ErrorMessage
                errors={errors}
                name="confirm"
                render={() => (
                  <p className="f6 red db lh-copy" data-testid="confirm-delete__modal-validation">
                    Text entered is incorrect, enter DELETE to continue.
                  </p>
                )}
              />
            </>
          )}
        </fieldset>
        <div>
          {isSubmitting ? (
            <div className="flex flex-column items-center pv3" data-testid="confirm-delete__modal-loading">
              <Spinner size="small" color="blue" />
            </div>
          ) : (
            <>
              <button type="submit" className="button critical w-100" data-testid="confirm-delete__modal-confirm">
                {btnTxt}
              </button>
              <button
                type="button"
                className="button plain db w-100 mt2"
                onClick={closeModal}
                data-testid="confirm-delete__modal-cancel"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default AsyncConfirmDelete;
